import { Info } from 'lucide-react';
import type { DecisionData, UrgencyLevel, CorrectnessLevel } from '../types';

interface AssessmentViewProps {
  decisionData: DecisionData;
  urgencyLevel: UrgencyLevel | null;
  correctnessLevel: CorrectnessLevel | null;
  onUrgencySelect: (level: UrgencyLevel) => void;
  onCorrectnessSelect: (level: CorrectnessLevel) => void;
}

export function AssessmentView({
  decisionData,
  urgencyLevel,
  correctnessLevel,
  onUrgencySelect,
  onCorrectnessSelect,
}: AssessmentViewProps) {
  const urgencyOptions: { value: UrgencyLevel; label: string; description: string }[] = [
    {
      value: 'high',
      label: 'High Urgency',
      description: 'Hours or a day. Immediate action required.',
    },
    {
      value: 'medium',
      label: 'Medium Urgency',
      description: 'This week. Some time available but not unlimited.',
    },
    {
      value: 'low',
      label: 'Low Urgency',
      description: 'Open timeline. Can take time to deliberate.',
    },
  ];

  const correctnessOptions: { value: CorrectnessLevel; label: string; description: string }[] = [
    {
      value: 'high',
      label: 'High Correctness Required',
      description: 'Consequences of being wrong are severe. High visibility, risk, or political cost.',
    },
    {
      value: 'medium',
      label: 'Medium Correctness Required',
      description: 'Some consequences if wrong, but manageable. Moderate visibility or risk.',
    },
    {
      value: 'low',
      label: 'Low Correctness Required',
      description: 'Low consequences if wrong. Easy to reverse or adjust. Low visibility or risk.',
    },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">{decisionData.decisionName}</h2>
        <p className="text-[var(--text-sec)] mb-6">{decisionData.context}</p>
      </div>

      {/* Urgency Assessment */}
      {!urgencyLevel && (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
          <div className="flex items-start gap-2 mb-4">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">Assess FAST Dimension</h3>
              <p className="text-sm text-[var(--text-sec)] mt-1">
                How urgent is this decision?
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {urgencyOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onUrgencySelect(option.value)}
                className="w-full p-4 border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] text-left transition-all"
              >
                <div className="font-semibold text-lg mb-1">{option.label}</div>
                <div className="text-sm text-[var(--text-sec)]">{option.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Correctness Assessment */}
      {urgencyLevel && !correctnessLevel && (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
          <div className="flex items-start gap-2 mb-4">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">Assess RIGHT Dimension</h3>
              <p className="text-sm text-[var(--text-sec)] mt-1">
                How 'right' does this decision need to be? Consider consequences of being wrong, visibility, risk, political cost, etc.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {correctnessOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onCorrectnessSelect(option.value)}
                className="w-full p-4 border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] text-left transition-all"
              >
                <div className="font-semibold text-lg mb-1">{option.label}</div>
                <div className="text-sm text-[var(--text-sec)]">{option.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


